/**
 * Summary card components (APP-002 §13).
 *
 * Reusable, state-aware cards built on the APP-001 design system. Each
 * supports loading (skeleton), value, supporting label, optional progress,
 * warning state, unavailable state, and an action link. They never fabricate
 * data: loading shows a skeleton, unavailable shows a neutral dash.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';

export interface MetricCardProps {
  /** Short label, e.g. "Requests used". */
  label: string;
  /** Primary value to render when loaded. */
  value: ReactNode;
  /** Secondary line, e.g. "8,800 remaining". */
  supporting?: ReactNode;
  /** When true, render a skeleton instead of the value. */
  loading?: boolean;
  /** When true, render an unavailable neutral dash (no fabricated value). */
  unavailable?: boolean;
  /** Optional progress value (clamped 0–100) + accessible label. */
  progress?: { value: number; ariaLabel: string; tone?: 'default' | 'warning' | 'danger' };
  /** Optional warning emphasis (color-independent; the text remains truthful). */
  warning?: boolean;
  className?: string;
}

/**
 * A single metric: label, value, supporting text, optional progress. The most
 * reusable card; composes the others.
 */
export function MetricCard({
  label,
  value,
  supporting,
  loading,
  unavailable,
  progress,
  warning,
  className,
}: MetricCardProps) {
  return (
    <Card className={cn(warning && 'border-warning/50', className)}>
      <CardHeader className="pb-2">
        <CardDescription>{label}</CardDescription>
        <CardTitle className="text-2xl">
          {loading ? (
            <Skeleton className="h-7 w-24" />
          ) : unavailable ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            value
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {progress && !loading && !unavailable ? (
          <Progress value={progress.value} label={progress.ariaLabel} tone={progress.tone} />
        ) : null}
        {supporting && !loading ? <p className="text-xs text-muted-foreground">{supporting}</p> : null}
      </CardContent>
    </Card>
  );
}

export interface PlanCardProps {
  planName: string | null | undefined;
  billingInterval?: string | null;
  /** Badge node (the caller renders the semantic badge from statusMapping). */
  badge?: ReactNode;
  loading?: boolean;
  /** Optional link to the plan/billing surface. */
  to?: string;
}

/** Renders the effective plan name + a status badge + optional interval. */
export function PlanCard({ planName, billingInterval, badge, loading, to }: PlanCardProps) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription>Current plan</CardDescription>
        <CardTitle className="text-2xl">
          {loading ? <Skeleton className="h-7 w-24" /> : planName ?? <span className="text-muted-foreground">—</span>}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex items-center gap-2">
        {badge}
        {billingInterval && !loading ? (
          <span className="text-xs text-muted-foreground capitalize">{billingInterval}</span>
        ) : null}
        {to && !loading ? (
          <Link to={to} className="ml-auto text-xs text-primary hover:underline">
            Manage
          </Link>
        ) : null}
      </CardContent>
    </Card>
  );
}

export interface StatusCardProps {
  title: string;
  children: ReactNode;
  action?: ReactNode;
}

/** A flexible status/summary card for account-state sections. */
export function StatusCard({ title, children, action }: StatusCardProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {children}
        {action ? <div className="flex flex-wrap items-center gap-2">{action}</div> : null}
      </CardContent>
    </Card>
  );
}

export interface ActionCardProps {
  title: string;
  description?: string;
  /** Primary call to action. */
  action: ReactNode;
  icon?: ReactNode;
  className?: string;
}

/** A next-action card with a clear primary CTA. */
export function ActionCard({ title, description, action, icon, className }: ActionCardProps) {
  return (
    <Card className={cn(className)}>
      <CardHeader className="flex-row items-start gap-3 space-y-0">
        {icon ? <div className="mt-0.5 text-primary">{icon}</div> : null}
        <div className="space-y-1">
          <CardTitle className="text-base">{title}</CardTitle>
          {description ? <CardDescription>{description}</CardDescription> : null}
        </div>
      </CardHeader>
      <CardContent>{action}</CardContent>
    </Card>
  );
}

/**
 * A responsive grid section grouping summary cards. Cards reflow cleanly from
 * 1 → 2 → 4 columns (APP-002 §23).
 */
export function SummarySection({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('grid gap-4 sm:grid-cols-2 lg:grid-cols-4', className)}>{children}</div>;
}
